/* Test people: the test system's one-click sign-in (Chris, 2026-10-16).
 *
 * Asks the server which made-up test people exist on this system. Only an
 * answer of 200 with a non-empty list shows anything. Live never asks: it is a
 * production build, and authService.listTestPeople sends no request there. A
 * server that is not a test system has no such address and answers 404. Either
 * way the sign-in page stays exactly as it always was, with nothing said to
 * the person. Whether this is a test system is the server's call
 * (backend/authentication/persona_sign_in.py); this panel never guesses.
 *
 * Clicking a person hands their key to `onPick`; the sign-in page signs in
 * and then carries on exactly as after a password sign-in.
 */
import React, { useEffect, useState } from 'react';
import HMSButton from './ui/HMSButton';
import { authService } from '../services/authService';
import './TestPeoplePanel.css';

// The server sends people already in area order; keep it, one group per area.
const groupByArea = (people) => {
  const groups = [];
  people.forEach((person) => {
    const last = groups[groups.length - 1];
    if (last && last.area === person.area) {
      last.people.push(person);
    } else {
      groups.push({ area: person.area, people: [person] });
    }
  });
  return groups;
};

function TestPeoplePanel({ onPick, disabled = false }) {
  const [people, setPeople] = useState([]);

  useEffect(() => {
    let mounted = true;
    authService.listTestPeople()
      .then((list) => {
        if (mounted) setPeople(list);
      })
      .catch((err) => {
        // 404 is a server that is not a test system: say nothing. Anything
        // else is a test system whose list broke; leave a trace for whoever
        // is testing (live never gets here, it never asks), but never
        // anything on the page.
        if (err && err.response && err.response.status === 404) return;
        console.error('Test people list could not load:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (people.length === 0) return null;

  return (
    <section className="test-people" aria-labelledby="test-people-title">
      <h3 id="test-people-title" className="test-people-title">Test people</h3>
      <p className="test-people-hint">Test system only. One click signs in as that person, no password.</p>
      <div className="test-people-list">
        {groupByArea(people).map((group) => (
          <div key={group.area} className="test-people-group" role="group" aria-label={group.area}>
            <h4 className="test-people-area">{group.area}</h4>
            {group.people.map((person) => (
              <HMSButton
                key={person.key}
                variant="ghost"
                size="compact"
                className="test-people-person"
                disabled={disabled}
                onClick={() => onPick(person.key)}
              >
                <span className="test-people-name">{person.name}</span>
                <span className="test-people-key">{person.key}</span>
                <span className="test-people-what">{person.what_they_can_do}</span>
              </HMSButton>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

export default TestPeoplePanel;
